import React, { memo, useState, useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import DatePicker from 'modules/views/shared/DatePicker';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import DuplicateWorkerModal from './DuplicateWorkerModal';
import Modal from 'modules/views/shared/Modal';
import NavigationButtons from 'modules/views/shared/NavigationButtons';

import useForm from '../../../../hooks/useForm';
import useSubmitOnEnter from '../../../../hooks/useSubmitOnEnter';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import basicInformationSchema from './schema';
import {
  canCreateUnverifiedWorkersInProject,
  prettifyErrorResponse,
  extractNationalNumber,
  isValidMobileNumber,
} from 'utils';
import { fetchWorkerDetails, searchWorker, setOnboarding, setTemp } from 'modules/state-mgmt/onboarding';
import { getSearchWorkerParams } from './utils';
import { useCreateWorker } from 'modules/views/pages/Registration/hooks';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard';

import { ROUTES } from 'constants/routes';
import { WORKER_NAME_MAX_LENGTH } from 'constants/common';
import { WorkerProfileField } from 'types';
import { WorkerInvitationStatus } from 'models/general';
import { useStylesDuplicateWorkerModal } from './styles';

// Helper to convert Moment objects to ISO strings for IndexedDB storage
const normalizeValuesForStorage = (values: any) => ({
  ...values,
  dateOfBirth: values.dateOfBirth
    ? typeof values.dateOfBirth === 'string'
      ? values.dateOfBirth
      : values.dateOfBirth?.format?.('YYYY-MM-DD')
    : null,
});

const BasicInformation = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canGoToPreviousStep, goToNextStep, goToPreviousStep } = useWizardStepNavigation();
  const {
    onboardingTemp,
    profileFieldConstraintsMap,
    languageCode,
    hasUnverifiedWorkerAccess,
    isOffline,
    isWorker,
    isWorkerLoggedIn,
  } = useAppSelector(state => ({
    onboardingTemp: state.onboarding.temp,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    languageCode: state.app?.languageCode,
    hasUnverifiedWorkerAccess: state.user.userId && !state.user.isWorker,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
    isWorkerLoggedIn: state.user?.userId && state.user?.isWorker,
  }));
  const [duplicateWorkerModal, setDuplicateWorkerModal] = useState({
    isOpen: false,
    duplicateWorkers: [],
    currentPage: 1,
    totalCount: 0,
    pageSize: 50,
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showUnverifiedWorkerWarning, setShowUnverifiedWorkerWarning] = useState(false);

  const canGoToRegistrationScreen =
    profileFieldConstraintsMap[WorkerProfileField.email]?.isVisible ||
    profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber]?.isVisible ||
    profileFieldConstraintsMap[WorkerProfileField.emailOrMobilePhoneNumber]?.isVisible;

  const shouldUserGoToRegistrationScreen =
    profileFieldConstraintsMap[WorkerProfileField.email]?.isMandatory ||
    profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber]?.isMandatory ||
    profileFieldConstraintsMap[WorkerProfileField.emailOrMobilePhoneNumber]?.isMandatory;
  const isDateOfBirthVisible = profileFieldConstraintsMap[WorkerProfileField.workerDateOfBirth]?.isVisible;
  const isEmailVisible = profileFieldConstraintsMap[WorkerProfileField.email]?.isVisible;
  const isMobilePhoneNumberVisible = profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber]?.isVisible;
  const isEmailOrMobilePhoneNumberVisible =
    profileFieldConstraintsMap[WorkerProfileField.emailOrMobilePhoneNumber]?.isVisible;

  const [isLoading, setIsLoading] = useState(false);
  const classes = useStylesDuplicateWorkerModal();

  const maxDate = moment();
  maxDate.subtract(18, 'years');

  const { handleCreateWorker } = useCreateWorker({
    onSuccess: () => setIsLoading(false),
    onFailure: () => setIsLoading(false),
  });

  const onProceedWithSelectedWorker = (worker: any) => {
    if (worker?.invitationStatus === WorkerInvitationStatus.NOT_INVITED) {
      setDuplicateWorkerModal({ ...duplicateWorkerModal, isOpen: false });
      if (hasUnverifiedWorkerAccess) {
        dispatch(fetchWorkerDetails({ workerId: worker.id, languageCode: languageCode }))
          .then(res => {
            setIsLoading(false);
            if (res.type.includes('fulfilled')) {
              if (shouldUserGoToRegistrationScreen) {
                goToNextStep();
              } else {
                navigate(ROUTES.PERSONAL_INFORMATION.path);
              }
            } else {
              enqueueSnackbar(prettifyErrorResponse(res?.payload), {
                variant: 'error',
              });
            }
          })
          .catch(err => {
            setIsLoading(false);
            enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
          });
      } else {
        setIsLoading(false);
        setShowUnverifiedWorkerWarning(true);
      }
    } else {
      const primaryContact = worker.userContacts?.find((contact: any) => contact.isPrimary);
      const email = primaryContact?.contactType === 0 ? primaryContact.contactValue : '';
      const mobilePhoneNumber = primaryContact?.contactType === 1 ? primaryContact.contactValue : '';
      // CH-4652: tag the project/employers already in `temp` with the worker being sent to log in,
      // so `useLogin` attaches them to it afterwards instead of dropping them on an empty My
      // Projects. Storing the id (not a flag) keeps a persisted leftover from a previously
      // abandoned flow from ever acting on a different worker. `setTemp` replaces `temp`
      // wholesale, hence the spread.
      dispatch(setTemp({ ...(onboardingTemp || {}), resumeProjectOnboarding: worker.id }));
      navigate(ROUTES.LOGIN.path, { state: { email, mobilePhoneNumber } });
    }
  };

  const { values, errors, isSubmitted, handleSubmit, setValue } = useForm({
    initialValues: {
      firstName: onboardingTemp?.firstName || '',
      lastName: onboardingTemp?.lastName || '',
      email: onboardingTemp?.email || '',
      mobilePhoneNumber: onboardingTemp?.mobilePhoneNumber || '',
      dateOfBirth: onboardingTemp?.dateOfBirth ? (moment(onboardingTemp.dateOfBirth) as any) : null,
    },
    validationSchema: basicInformationSchema(t),
    onSubmit: values => {
      if (values.email?.trim() || extractNationalNumber(values.mobilePhoneNumber) || values.dateOfBirth) {
        const normalizedValues = normalizeValuesForStorage({
          ...values,
          dateOfBirth: isDateOfBirthVisible ? values.dateOfBirth : null,
          email: isEmailVisible || isEmailOrMobilePhoneNumberVisible ? values.email : null,
          mobilePhoneNumber:
            (isMobilePhoneNumberVisible || isEmailOrMobilePhoneNumberVisible) &&
            isValidMobileNumber(values.mobilePhoneNumber)
              ? values.mobilePhoneNumber
              : null,
        });
        if (isOffline && !isWorker) {
          if (canGoToRegistrationScreen) {
            dispatch(setTemp({ ...(onboardingTemp || {}), ...normalizedValues }));
            goToNextStep();
          } else {
            dispatch(
              setOnboarding({
                ...normalizedValues,
                invitationStatus: WorkerInvitationStatus.NOT_INVITED,
                temp: {
                  ...onboardingTemp,
                  ...normalizedValues,
                },
                uploadingDetails: {
                  ...onboardingTemp?.uploadingDetails,
                  pendingUploads: {
                    ...(onboardingTemp?.uploadingDetails?.pendingUploads || {}),
                    workerCreation: true,
                    employers: true,
                  },
                  isOffline: true,
                },
              }),
            );
            navigate(ROUTES.TAKE_PHOTO.path);
          }
          return;
        }

        if (isWorker && !canGoToRegistrationScreen) {
          setIsLoading(false);
          setShowUnverifiedWorkerWarning(true);
        } else {
          setIsLoading(true);
          const searchParams = getSearchWorkerParams({
            ...(onboardingTemp || {}),
            ...values,
            languageCode: languageCode,
          });
          const paginationParams = {
            pageNumber: 1,
            pageSize: 50,
          };
          dispatch(searchWorker({ ...searchParams, ...paginationParams }))
            .then(res => {
              if (res.type.includes('fulfilled')) {
                dispatch(setTemp({ ...(onboardingTemp || {}), ...normalizedValues }));
                const response = res.payload as { items: any[]; totalCount: number };
                if (response?.items?.length > 0) {
                  setDuplicateWorkerModal({
                    isOpen: true,
                    duplicateWorkers: response.items,
                    currentPage: 1,
                    totalCount: response.totalCount,
                    pageSize: 50,
                  });
                } else {
                  if (canGoToRegistrationScreen) {
                    goToNextStep();
                  } else if (hasUnverifiedWorkerAccess) {
                    handleCreateWorker({
                      ...(onboardingTemp || {}),
                      ...normalizedValues,
                    });
                  } else {
                    setIsLoading(false);
                    setShowUnverifiedWorkerWarning(true);
                  }
                }
              } else {
                setIsLoading(false);
              }
            })
            .catch(err => setIsLoading(false));
        }
      }
    },
  });

  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  const onAddNewWorker = () => {
    setDuplicateWorkerModal({ ...duplicateWorkerModal, isOpen: false });
    if (canGoToRegistrationScreen) {
      goToNextStep();
    } else if (hasUnverifiedWorkerAccess) {
      setIsLoading(true);
      const normalizedValues = normalizeValuesForStorage({
        ...values,
        dateOfBirth: isDateOfBirthVisible ? values.dateOfBirth : null,
        email: isEmailVisible || isEmailOrMobilePhoneNumberVisible ? values.email : null,
        mobilePhoneNumber:
          (isMobilePhoneNumberVisible || isEmailOrMobilePhoneNumberVisible) &&
          isValidMobileNumber(values.mobilePhoneNumber)
            ? values.mobilePhoneNumber
            : null,
      });
      handleCreateWorker({
        ...(onboardingTemp || {}),
        ...normalizedValues,
      });
    } else {
      setIsLoading(false);
      setShowUnverifiedWorkerWarning(true);
    }
  };

  const loadMoreWorkers = () => {
    if (isLoadingMore || duplicateWorkerModal.duplicateWorkers.length >= duplicateWorkerModal.totalCount) {
      return;
    }

    setIsLoadingMore(true);
    const nextPage = duplicateWorkerModal.currentPage + 1;
    const searchParams = getSearchWorkerParams({
      ...(onboardingTemp || {}),
      ...values,
      languageCode: languageCode,
    });
    const paginationParams = {
      pageNumber: nextPage,
      pageSize: duplicateWorkerModal.pageSize,
    };

    dispatch(searchWorker({ ...searchParams, ...paginationParams }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          const response = res.payload as { items: any[]; totalCount: number };
          setDuplicateWorkerModal(prev => ({
            ...prev,
            duplicateWorkers: [...prev.duplicateWorkers, ...response.items],
            currentPage: nextPage,
          }));
        }
        setIsLoadingMore(false);
      })
      .catch(err => {
        setIsLoadingMore(false);
      });
  };

  const showAtLeastOneWarning =
    isSubmitted && !values.email?.trim() && !extractNationalNumber(values.mobilePhoneNumber) && !values.dateOfBirth;

  const fields = useMemo(
    () => [
      {
        name: t('basicInformation.firstName'),
        id: 'firstName',
        isRequired: true,
      },
      {
        name: t('basicInformation.lastName'),
        id: 'lastName',
        isRequired: true,
      },
      {
        name: t('basicInformation.email'),
        id: 'email',
        isRequired: false,
      },
      {
        name: t('basicInformation.phoneNumber'),
        id: 'mobilePhoneNumber',
        isRequired: false,
      },
      {
        name: t('basicInformation.dateOfBirth'),
        id: 'dateOfBirth',
        isRequired: false,
      },
    ],
    [t],
  );

  const content = (
    <div>
      <ContentHeader title={t('basicInformation.title')} subtitle={t('basicInformation.subtitle')} />
      <Grid container spacing={3} style={{ marginTop: 28 }}>
        <Grid item xs={12} lg={6}>
          <TextField
            label={t('basicInformation.firstName')}
            autoFocus={true}
            name="firstName"
            required={true}
            inputProps={{ maxLength: WORKER_NAME_MAX_LENGTH }}
            value={values.firstName}
            error={errors.firstName?.message}
            testId="firstName-input"
            onChange={e => setValue('firstName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            label={t('basicInformation.lastName')}
            name="lastName"
            required={true}
            inputProps={{ maxLength: WORKER_NAME_MAX_LENGTH }}
            value={values.lastName}
            error={errors.lastName?.message}
            testId="lastName-input"
            onChange={e => setValue('lastName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            label={t('basicInformation.email')}
            name="email"
            value={values.email}
            error={errors.email?.message}
            testId="email-input"
            onChange={e => setValue('email', e.target.value?.trim())}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <MobileInput
            label={t('basicInformation.phoneNumber')}
            placeholder={t('basicInformation.phoneNumber')}
            value={values.mobilePhoneNumber}
            error={errors.mobilePhoneNumber?.message}
            testId="mobilePhoneNumber-input"
            onChange={value => setValue('mobilePhoneNumber', value.phone)}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <DatePicker
            inputLabel={t('basicInformation.dateOfBirth')}
            value={values.dateOfBirth}
            minDate={moment('01/01/1900')}
            maxDate={maxDate}
            defaultCalendarMonth={maxDate}
            testId="dateOfBirth-picker"
            onChange={(date, value) => {
              setValue('dateOfBirth', date);
            }}
            onKeyDown={submitOnEnter}
            error={errors.dateOfBirth?.message}
          />
        </Grid>
      </Grid>
      <Typography className={classes.errorMessage} color="error" variant="subtitle2" align="left">
        {showAtLeastOneWarning ? t('basicInformation.errors.atLeastOneMandatory') : ''}
      </Typography>

      <NavigationButtons
        secondaryButtonProps={{
          // isHidden: !canGoToPreviousStep,
          onClick: () => navigate(ROUTES.SELECT_PROJECT.path),
          testId: 'basic-info-back-button',
        }}
        primaryButtonProps={{
          isLoading,
          hasEndArrow: true,
          testId: 'basic-info-next-button',
          onClick: handleSubmit,
        }}
      />
    </div>
  );

  return (
    <>
      <WithWizard disableNavigation={isLoading} fields={fields} content={content} />
      {duplicateWorkerModal.isOpen && (
        <DuplicateWorkerModal
          duplicateWorkers={duplicateWorkerModal.duplicateWorkers}
          onClose={() => {
            setIsLoading(false);
            setDuplicateWorkerModal({ ...duplicateWorkerModal, isOpen: false });
          }}
          onProceedWithSelectedWorker={onProceedWithSelectedWorker}
          onAddNewWorker={onAddNewWorker}
          isWorker={isWorker} //no user or logged in user is worker
          hasMore={duplicateWorkerModal.duplicateWorkers.length < duplicateWorkerModal.totalCount}
          onLoadMore={loadMoreWorkers}
          isLoadingMore={isLoadingMore}
        />
      )}
      {showUnverifiedWorkerWarning && (
        <Modal
          title={t('common.warning')}
          Content={<p>{t('basicInformation.unverifiedWorkerWarning')}</p>}
          actionButtons={[
            {
              label: t('common.close'),
              onClick: () => {
                setShowUnverifiedWorkerWarning(false);
              },
              testId: 'basic-info-unverified-warning-close-button',
            },
          ]}
          onClose={() => setShowUnverifiedWorkerWarning(false)}
        />
      )}
    </>
  );
};

export default memo(BasicInformation);
